// A data-source pill next to an agent step. Deliberately not a card: dashed,
// rounded, on the canvas background, so it reads as "where the agent's tools
// come from" rather than as another step.

import { Handle, Position, type NodeProps } from "@xyflow/react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Database, TriangleAlert } from "lucide-mui";
import { summarizeDataSource } from "./dataSources.ts";
import { DS_HEIGHT, DS_WIDTH } from "./layout.ts";
import type { DataSourceNode as DataSourceNodeType } from "./types.ts";

export const UNCONFIGURED_TITLE = "Not configured for this organization";

export default function DataSourceNode({ data, selected }: NodeProps<DataSourceNodeType>) {
  const unconfigured = !data.implicit && !data.configured;
  const summary = summarizeDataSource(data);
  const tone = unconfigured ? "warning.main" : "text.disabled";

  return (
    <Box
      className="nodrag"
      sx={{
        width: DS_WIDTH,
        height: DS_HEIGHT,
        boxSizing: "border-box",
        px: 1.25,
        borderRadius: "999px",
        border: "1px dashed",
        borderColor: selected ? "primary.main" : tone,
        bgcolor: "background.default",
        boxShadow: selected ? "0 0 0 3px rgba(var(--mui-palette-primary-mainChannel) / 0.25)" : "none",
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        outline: "none",
        cursor: "pointer",
        transition: "box-shadow 120ms, border-color 120ms",
      }}
    >
      <Box sx={{ display: "flex", color: tone, flexShrink: 0 }}>
        <Database fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography
          variant="overline"
          sx={{ display: "block", lineHeight: 1.2, fontSize: "0.55rem", color: "text.disabled" }}
        >
          Data source
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontSize: "0.75rem", lineHeight: 1.3, color: summary.error ? "error.main" : "text.primary" }}
          noWrap
        >
          {summary.text}
        </Typography>
      </Box>
      {unconfigured && (
        <Tooltip title={UNCONFIGURED_TITLE}>
          <Box sx={{ display: "flex", color: "warning.main", flexShrink: 0 }}>
            <TriangleAlert fontSize="small" />
          </Box>
        </Tooltip>
      )}
      <Handle
        id="out"
        type="source"
        position={Position.Right}
        isConnectable={false}
        style={{
          width: 6,
          height: 6,
          minWidth: 0,
          minHeight: 0,
          background: "var(--mui-palette-background-paper)",
          border: "1px solid var(--mui-palette-text-disabled)",
        }}
      />
    </Box>
  );
}
